<template>
  <!-- class: also opened from the Edit Task slide-out, which sits above Vuetify's dialog stack -->
  <v-dialog :value="model.active" width="520" persistent class="ds-dialog-above-slideout" @keydown.esc="closeDialog">
    <v-card>
      <div class="d-flex justify-space-between align-center pr-2">
        <v-card-title class="text-h6 font-weight-bold">
          <v-icon left>mdi-plus</v-icon>
          Add Document
        </v-card-title>
        <v-btn icon aria-label="Close" @click="closeDialog"><v-icon>fa-close</v-icon></v-btn>
      </div>
      <v-divider></v-divider>

      <v-card-text class="pt-5 overflow-y-auto" style="max-height: 80vh">
        <div class="modal-body" :class="{ 'amis-body': isAMIS }">
          <!-- Document Scope Selection (a task's documents belong to its facility) -->
          <div v-if="!task_id" class="scope-section mb-4">
            <label class="section-label d-flex align-center mb-2">
              <span class="text-subtitle-2 font-weight-medium">Document Scope</span>
            </label>
            <div class="scope-toggle d-flex mb-2">
              <v-btn
                :color="scopeType === 'Facility' ? 'primary' : 'grey lighten-1'"
                :outlined="scopeType !== 'Facility'"
                small
                class="flex-grow-1 mr-1"
                @click="setScopeType('Facility')">
                <v-icon small left>mdi-domain</v-icon>
                Facility
              </v-btn>
              <v-btn
                :color="scopeType === 'FacilityGroup' ? 'primary' : 'grey lighten-1'"
                :outlined="scopeType !== 'FacilityGroup'"
                small
                class="flex-grow-1 ml-1"
                :disabled="!facilityGroups.length"
                :loading="facilityGroupsLoading"
                @click="setScopeType('FacilityGroup')">
                <v-icon small left>mdi-account-group-outline</v-icon>
                Group
              </v-btn>
            </div>

            <!-- Facility Group Selector (when FacilityGroup selected) -->
            <v-autocomplete
              v-if="scopeType === 'FacilityGroup'"
              v-model="selectedFacilityGroupId"
              :items="facilityGroups"
              item-text="name"
              item-value="id"
              label="Select Facility Group"
              outlined
              dense
              hide-details
              class="mb-2"
              prepend-inner-icon="mdi-folder-multiple">
            </v-autocomplete>

            <!-- Info Alert for Group Scope -->
            <v-alert
              v-if="scopeType === 'FacilityGroup'"
              type="info"
              dense
              outlined
              class="mb-0 text-caption">
              <v-icon small left color="info">mdi-information</v-icon>
              Documents will be visible across all facilities in this group.
            </v-alert>
          </div>

          <v-sheet
            height="180px"
            class="drag-area d-flex align-center flex-column justify-center rounded-lg"
            :class="{ 'drag-area--over': isDragOver }"
            style="border-style: dashed"
            outlined
            role="button"
            tabindex="0"
            aria-label="Choose files to upload, or drop them here"
            @click="openFilePicker"
            @keydown.enter.prevent="openFilePicker"
            @keydown.space.prevent="openFilePicker"
            @dragenter.prevent="isDragOver = true"
            @dragover.prevent="isDragOver = true"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop">
            <v-icon class="mb-2" x-large :color="isDragOver ? 'primary' : undefined">mdi-file-upload-outline</v-icon>
            <div>
              <!-- Security: Only allow safe document types -->
              <input
                ref="fileInput"
                type="file"
                multiple
                style="display: none"
                :accept="acceptedTypes"
                @change="onFilesPicked" />
              {{ isDragOver ? 'Drop to add files' : 'Drag and drop or' }}
              <a v-if="!isDragOver" class="text-decoration-underline mx-1" @click.stop="openFilePicker">choose files</a>
            </div>
            <div class="text-caption mt-1 drag-area__hint">
              PDF, Office, images, CAD (DWG, DXF, RVT, IFC) · up to {{ maxFiles }} files
            </div>
          </v-sheet>
          <div style="margin: 7px; position: relative" v-if="isAMIS">
            <CustomSelectBox
              :id="'upload-attachment-section-filter'"
              :is_multiple="false"
              :with_search="false"
              :show_checkbox="false"
              :options="types"
              :custom_icon_class="'fas fa-folder-open'"
              @filter_check_changed="filterCheckChanged">
            </CustomSelectBox>
          </div>

          <template v-if="selected.length">
            <div class="d-flex align-center mt-4 mb-1 selected-summary">
              <span class="text-subtitle-2">{{ selected.length }} {{ selected.length === 1 ? 'file' : 'files' }}</span>
              <span class="text-caption ml-2 selected-summary__size">{{ getSize(totalSize) }}</span>
              <v-spacer></v-spacer>
              <v-btn x-small text @click="clearSelection">Clear all</v-btn>
            </div>
            <v-list dense class="selected-files py-0">
              <v-list-item
                v-for="file in selected"
                :key="fileKey(file)"
                dense
                class="bg1 mb-1 rounded-lg">
                <v-list-item-icon class="mr-3 my-auto">
                  <v-icon small>{{ fileIcon(file.name) }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title :title="file.name">{{ file.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ getSize(file.size) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon small color="error" :aria-label="`Remove ${file.name}`" @click="remove(file)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </template>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn color="red" elevation="0" text @click="closeDialog"> Cancel </v-btn>
        <v-spacer></v-spacer>
        <span v-if="selected.length" class="text-caption mr-3 upload-note">Uploads run in the background</span>
        <v-btn
          color="primary"
          elevation="0"
          :disabled="!canSubmit"
          @click="submit">
          <v-icon small left>mdi-cloud-upload-outline</v-icon>
          Upload{{ selected.length > 1 ? ` ${selected.length} files` : '' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { validateFile, getFileExtension } from '@/utils/fileValidation';
  import CustomSelectBox from '../common/customSelectBox.vue';
  import groupsService from '@/features/Groups/service/groups.service';
  import { log as debugLog, error as debugError } from '@/utils/debugLogger';

  /** Extensions offered by the file picker (the blocklist in fileValidation still applies to drops). */
  const ACCEPTED_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.tif,.dwg,.dxf,.rvt,.ifc,.avi,.mp4,.mov,.mkv,.wmv,.webm,.m4v,.mpg,.mpeg,.mp3,.wav,.m4a,.aac,.ogg,.flac,.wma';
  /** Files per upload: they go to the API as one multipart request. */
  const MAX_FILES = 50;

  const FILE_ICONS = {
    pdf: 'mdi-file-pdf-box',
    doc: 'mdi-file-word-box',
    docx: 'mdi-file-word-box',
    xls: 'mdi-file-excel-box',
    xlsx: 'mdi-file-excel-box',
    csv: 'mdi-file-delimited-outline',
    ppt: 'mdi-file-powerpoint-box',
    pptx: 'mdi-file-powerpoint-box',
    png: 'mdi-file-image-outline',
    jpg: 'mdi-file-image-outline',
    jpeg: 'mdi-file-image-outline',
    gif: 'mdi-file-image-outline',
    bmp: 'mdi-file-image-outline',
    tif: 'mdi-file-image-outline',
    tiff: 'mdi-file-image-outline',
    dwg: 'mdi-file-cad',
    dxf: 'mdi-file-cad',
    rvt: 'mdi-file-cad',
    ifc: 'mdi-file-cad',
  };

  /**
   * Picks files and hands them to the background upload queue (DOCUMENT_UPLOADS). Two homes:
   *  - a facility's documents (default): scope selector (facility or group), optional AMIS type,
   *    `parent_id` links the documents to that tag
   *  - a task's documents (`task_id`): facility scope only, documents carry the task id and
   *    `document_type_id`; the task tab reloads on DOCUMENTS_UPLOADED
   */
  export default {
    name: 'AddDocumentPopup',
    props: ['parent_type', 'parent_id', 'task_id', 'document_type_id'],
    data() {
      return {
        selected: [],
        isDragOver: false,
        masterDocsFolder: 'MasterDocs',
        acceptedTypes: ACCEPTED_TYPES,
        maxFiles: MAX_FILES,
        types: [],
        model: {
          active: false,
        },
        // Scope selection for cross-facility documents
        scopeType: 'Facility',
        selectedFacilityGroupId: null,
        facilityGroups: [],
        facilityGroupsLoading: false,
      };
    },
    methods: {
      async open() {
        this.selected = [];
        this.isDragOver = false;
        this.scopeType = 'Facility';
        this.selectedFacilityGroupId = null;
        this.types = this.buildTypes(this.documentTypes);
        this.model.active = true;

        if (!this.task_id) await this.loadFacilityGroups();
      },
      reset() {
        this.model.active = false;
        this.selected = [];
        this.isDragOver = false;
        // Reset scope to default
        this.scopeType = 'Facility';
        this.selectedFacilityGroupId = null;
        this.$store.commit('SET_UPLOAD_SCOPE_TYPE', 'Facility');
        this.$store.commit('SET_UPLOAD_FACILITY_GROUP_ID', null);
      },
      async closeDialog() {
        if (this.selected.length) {
          const confirmed = await this.$dialog.confirm({
            title: 'Discard selected files?',
            text: `${this.selected.length} selected ${this.selected.length === 1 ? 'file has' : 'files have'} not been uploaded.`,
            actions: {
              false: 'Keep editing',
              true: { text: 'Discard', color: 'error' },
            },
          });
          if (!confirmed) return;
        }
        this.reset();
      },
      clearSelection() {
        this.selected = [];
      },
      /**
       * Hands the files to the background upload queue and closes, so the user keeps working
       * while they upload. Progress, cancel and retry live in DocumentUploadTray.
       */
      submit() {
        if (!this.canSubmit) return;

        // Validate scope selection
        if (this.scopeType === 'FacilityGroup' && !this.selectedFacilityGroupId) {
          this.$dialog.notify.warning('Please select a facility group.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        if (this.task_id) {
          this.submitForTask();
          return;
        }

        let documentTypeId = this.types.find((t) => t.isChecked)?.id;
        if (documentTypeId) {
          this.$store.commit('SET_PREDEFINED_DOCUMENT_INFO', {
            documentTypeId: documentTypeId,
            forgeTagsIds: this.parent_id ? [this.parent_id] : [],
            revisionNumber: '0',
          });
        }

        // Prepare scope options for cross-facility documents
        const scopeOptions = {
          scopeType: this.scopeType,
          facilityGroupId: this.scopeType === 'FacilityGroup' ? this.selectedFacilityGroupId : null,
        };
        const group = this.facilityGroups.find((g) => g.id === scopeOptions.facilityGroupId);

        debugLog('AddDocumentPopup', 'Queueing upload with scope:', scopeOptions);

        this.$store.dispatch('DOCUMENT_UPLOADS/ENQUEUE', {
          files: this.selected,
          folder: this.masterDocsFolder,
          scopeOptions,
          predefined: this.$store.getters['GET_PREDEFINED_DOCUMENT_INFO'],
          scopeLabel: group ? `Group: ${group.name}` : '',
        });

        this.reset();
      },
      /**
       * A task's documents: facility scope, the task id on every document, and the type the tab
       * chose. Nothing goes through the facility's predefined-info state, so a tag panel left open
       * elsewhere cannot leak its tag onto the task's documents.
       */
      submitForTask() {
        const taskId = this.task_id;
        debugLog('AddDocumentPopup', 'Queueing upload for task:', taskId);

        this.$store.dispatch('DOCUMENT_UPLOADS/ENQUEUE', {
          files: this.selected,
          folder: this.masterDocsFolder,
          documentTypeId: this.document_type_id || null,
          scopeOptions: { scopeType: 'Facility', facilityGroupId: null },
          predefined: { taskId, forgeTagsIds: [], revisionNumber: '0' },
          scopeLabel: 'Task',
          context: { taskId },
        });

        this.reset();
      },

      /**
       * Load facility groups for the scope selector dropdown.
       */
      async loadFacilityGroups() {
        this.facilityGroupsLoading = true;
        try {
          const response = await groupsService.get({ itemsPerPage: -1 });
          this.facilityGroups = response?.data?.items || response?.data || [];
          debugLog('AddDocumentPopup', 'Loaded facility groups:', this.facilityGroups.length);
        } catch (err) {
          debugError('AddDocumentPopup', 'Failed to load facility groups:', err);
          this.facilityGroups = [];
        } finally {
          this.facilityGroupsLoading = false;
        }
      },

      /**
       * Set scope type and clear opposite selection
       */
      setScopeType(type) {
        this.scopeType = type;
        if (type === 'Facility') {
          this.selectedFacilityGroupId = null;
        }
        // Update store for other components that might need this
        this.$store.commit('SET_UPLOAD_SCOPE_TYPE', type);
        this.$store.commit('SET_UPLOAD_FACILITY_GROUP_ID', type === 'FacilityGroup' ? this.selectedFacilityGroupId : null);
      },
      openFilePicker() {
        this.$refs.fileInput?.click();
      },
      remove(file) {
        this.selected = this.selected.filter((f) => f !== file);
      },
      onFilesPicked(event) {
        this.addFiles(event.target.files);
        // Clear the input so picking the same file again still fires change.
        event.target.value = '';
      },
      onDragLeave(event) {
        // dragleave also fires when moving over a child element; only reset on leaving the zone.
        if (!event.currentTarget.contains(event.relatedTarget)) this.isDragOver = false;
      },
      onDrop(event) {
        this.isDragOver = false;
        this.addFiles(event.dataTransfer?.files);
      },
      /** Adds files to the selection: validated, de-duplicated, capped at MAX_FILES. */
      addFiles(fileList) {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;

        const known = new Set(this.selected.map(this.fileKey));
        const rejected = [];
        let duplicates = 0;
        let overLimit = 0;
        const added = [];

        incoming.forEach((file) => {
          const result = validateFile(file);
          if (!result.valid) {
            rejected.push(`${file.name}: ${result.error}`);
          } else if (file.size === 0) {
            rejected.push(`${file.name}: file is empty`);
          } else if (known.has(this.fileKey(file))) {
            duplicates++;
          } else if (this.selected.length + added.length >= MAX_FILES) {
            overLimit++;
          } else {
            known.add(this.fileKey(file));
            added.push(file);
          }
        });

        this.selected = [...this.selected, ...added];

        if (rejected.length) {
          this.$dialog.notify.warning(`Not added — ${rejected.join('; ')}`, { position: 'top-right', timeout: 6000 });
        }
        if (overLimit) {
          this.$dialog.notify.warning(`Up to ${MAX_FILES} files per upload. ${overLimit} not added.`, {
            position: 'top-right',
            timeout: 5000,
          });
        }
        if (duplicates) {
          this.$dialog.notify.info(`${duplicates} already selected ${duplicates === 1 ? 'file was' : 'files were'} skipped.`, {
            position: 'top-right',
            timeout: 3000,
          });
        }
      },
      fileKey(file) {
        return `${file.name}|${file.size}|${file.lastModified}`;
      },
      fileIcon(name) {
        return FILE_ICONS[getFileExtension(name).replace('.', '')] || 'mdi-file-document-outline';
      },
      getSize(size) {
        if (size < 1024) {
          return size + ' B';
        } else if (size < 1024 * 1024) {
          return (size / 1024).toFixed(1) + ' KB';
        } else if (size < 1024 * 1024 * 1024) {
          return (size / (1024 * 1024)).toFixed(1) + ' MB';
        } else {
          return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
        }
      },
      buildTypes(documentTypes) {
        return documentTypes.map((d) => ({ ...d, isChecked: d.name === this.defaultDocumentType }));
      },
      filterCheckChanged({ option }) {
        this.types.forEach((t) => (t.isChecked = false));
        option.isChecked = true;
      },
    },
    computed: {
      canSubmit() {
        return this.selected.length > 0 && !(this.scopeType === 'FacilityGroup' && !this.selectedFacilityGroupId);
      },
      totalSize() {
        return this.selected.reduce((sum, f) => sum + f.size, 0);
      },
      documentTypes: function () {
        let types = this.$store.getters['GET_DOCUMENT_TYPES'] ?? [];
        return [...types];
      },
      defaultDocumentType: {
        get() {
          return this.$store.getters['GET_DEFAULT_DOCUMENT_TYPE'];
        },
        set(value) {
          this.$store.commit('SET_DEFAULT_DOCUMENT_TYPE', value);
        },
      },
      isAMIS() {
        return !!this.$store.state.forgeSettings?.isAMIS;
      },
    },
    watch: {
      documentTypes: {
        handler: function (value) {
          if (!value?.length) return;
          this.types = this.buildTypes(value);
        },
        deep: true,
      },
    },
    components: { CustomSelectBox },
  };
</script>

<style>
  .add-document-popup-content {
    display: flex;
    flex-direction: column;
  }

  .drag-area {
    cursor: pointer;
    transition: background-color 150ms ease, border-color 150ms ease;
  }

  .drag-area:hover,
  .drag-area:focus-visible {
    background-color: var(--v-bg1-base);
    outline: none;
  }

  .drag-area.drag-area--over {
    border-color: var(--v-primary-base) !important;
    background-color: var(--ds-surface-active);
  }

  .drag-area__hint,
  .selected-summary__size,
  .upload-note {
    opacity: 0.65;
  }

  .selected-files {
    max-height: 260px;
    overflow-y: auto;
    background: transparent !important;
  }

  .add-document-popup .add-document-popup-header {
    background: var(--v-primary-base);
    border-radius: 5px 5px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0px 10px;
  }

  .add-document-popup .add-document-popup-header p {
    font-weight: bold;
    font-size: 20px;
    margin: 10px;
    color: #f2f2f2;
  }

  .add-document-popup .add-document-popup-header .fas.fa-times {
    font-size: 20px;
    color: var(--v-primary-lighten3);
    padding: 5px;
  }

  .add-document-popup .add-document-popup-header .fas.fa-times:hover {
    color: white;
  }

  .add-document-popup .uploaded-area {
    margin: 10px;
  }

  .add-document-popup .modal-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px 25px;
  }

  .add-document-popup .wide-btn {
    margin: 0;
  }

  .file-card {
    border: 1px solid #c7c7c7;
    border-radius: 5px;
    padding: 10px;
    display: flex;
    column-gap: 15px;
    box-shadow: 0px 0px 2px rgb(0 0 0 / 25%);
    align-items: center;
    margin-top: 10px;
  }

  .file-card i {
    cursor: pointer;
    padding: 5px;
    font-size: 18px;
  }
  .file-card i:hover {
    color: var(--v-primary-base);
  }

  .file-card .file-size {
    color: #a1a1a1;
    font-size: 12px;
    position: relative;
    left: 3px;
    bottom: 4px;
  }

  .uploaded-area {
    max-height: 400px;
    background: white;
    padding: 5px 25px;
  }

  .add-document-popup .wide-btn.disabled {
    background: #dadada;
    pointer-events: none;
  }

  .modal-body.amis-body {
    overflow: visible;
  }

  .modal-body.amis-body .select-box-container .select-box-div {
    width: 100%;
  }

  .model-body.amis-body .select-box-btn {
    font-size: 18px;
  }

  /* Scope selection styles */
  .scope-section {
    background-color: var(--v-bg1-lighten1);
    padding: 12px;
    border-radius: 8px;
    border: 1px solid var(--v-bg1-darken1);
  }

  .scope-toggle {
    display: flex;
    gap: 4px;
  }

  .scope-toggle .v-btn {
    text-transform: none;
  }

  .section-label {
    color: var(--v-text-base);
  }
</style>
